import { getVisualizationType, subTypes, types } from "../../Editor/itemTypes";
import importAnswer from "../Survey/importAnswer";

const getLineChartWithMissings = ({ allBeeps, table }) =>
  allBeeps.map((beep) => {
    let obj = {};
    const beepTable =
      beep?.table ?? // answered table
      table.map((row) => ({
        ...row,
        score: null,
        scoreArr: null,
        primaryScore: null,
      })); // missed beep table
    beepTable.forEach((val) => {
      obj[val.id + "#id"] = val.id;
      obj[val.id + "#absolute"] = val.primaryScore;
      obj[val.id + "#relative"] = val.score;
      obj[val.id + "#title"] = val.label;
      obj[val.id + "#type"] = val.type;
    });
    return {
      ...obj,
      time: beep.time,
    };
  });

const getLineChart = (beeps) =>
  beeps.map((beep) => {
    let obj = {};
    beep.table.forEach((val) => {
      obj[val.id + "#id"] = val.id;
      obj[val.id + "#absolute"] = val.primaryScore;
      obj[val.id + "#relative"] = val.score;
      obj[val.id + "#title"] = val.label;
      obj[val.id + "#type"] = val.type;
    });
    return {
      ...obj,
      time: beep.time,
    };
  });

export default function importAnswers({ sentBeeps, beepList, interactions }) {
  //console.log("importAnswers");
  let answers = [];
  beepList?.forEach((beep) => {
    const answer = importAnswer({ beep, interactions });
    answers.push(answer);
  });
  //console.log(answers)
  const t0 = performance.now();
  const answeredBeeps = answers.sort((a, b) => a.time - b.time); // Sort numbers in ascending order

  const allBeeps = sentBeeps
    ? sentBeeps.map((sentBeep) => {
        const findAnswer = answers.filter(
          (answer) => answer.beepId === sentBeep.id
        )?.[0];
        if (findAnswer) return findAnswer;
        return sentBeep;
      })
    : []; // merge answered beeps with missed beeps

  /**
  @typedef BeepEntity
  @type {object}
  @property {string} id - Unique label id
  @property {string} label - Label title
  @property {number} number - The position when this question was asked
  @property {boolean} isQuantitative - Is the label quantitative?
  @property {string} question - Question title
  @property {number} visPri - The visualization importance
  @property {Array} scoreArr - The answer value in an array
  @property {Array || number} primaryScore - Multiple choice answers indexes in an array and slider values as a number
  @property {string || number} score - The relative answer value
 */

  /**
  @typedef LabelEntity
  @type {object}
  @property {number} time - utc timestamp in ms.
  @property {BeepEntity} data - the answers object.
 */

  /** @type {Object.<string, LabelEntity>} An object with string keys and LabelEntity values: */
  let labels = {}; // Tracing all unique labels

  /** @type {Array.<BeepEntity>}  An array of BeepEntity instances */
  let verticalTable = [];

  let corrMatrix = {};

  let occurrences = {};

  // get all the unique labels
  answeredBeeps.forEach((beep, index) => {
    let verticalTableRow = { id: index, time: beep.time };
    beep.table.forEach((row) => {
      verticalTableRow[row.id] = row.scoreArr;
      if (row.isQuantitative) {
        if (!(row.id in corrMatrix)) {
          corrMatrix[row.id] = Array.from(
            { length: answeredBeeps.length },
            () => NaN
          );
        }
        if (row.id in corrMatrix) {
          corrMatrix[row.id][index] = row.score;
        }
      }

      if (!(row.id in occurrences)) {
        occurrences[row.id] = 0;
      }
      if (row.id in occurrences) {
        occurrences[row.id] = occurrences[row.id] + 1;
      }

      // pick the most recent label in the beepsList
      if (labels?.[row.id] === undefined || labels[row.id].time < beep.time) {
        labels[row.id] = { time: beep.time, data: row };
      }
    });
    verticalTable.push(verticalTableRow);
  });

  // Get more information about the survey (get the latest beep for this kind of information)
  const lastBeep = answeredBeeps.at(-1) ?? {
    importantLabelProps: [],
    about: {},
  };

  let table = [];
  let quanti = [];
  let quali = [];
  let openAnswers = [];
  let choiceAnswers = [];
  let timeAnswers = []; // time labels
  Object.keys(labels).forEach((label) => {
    const data = labels[label].data;
    table.push(data);
    if (getVisualizationType(data.type) === types.open) openAnswers.push(data);
    if (getVisualizationType(data.type) === types.choice)
      choiceAnswers.push(data);
    if (data.type === subTypes.time) timeAnswers.push(data);
    if (data.isQuantitative) {
      quanti.push(data);
    } else {
      quali.push(data);
    }
  });

  const result = {
    history: answeredBeeps,
    importantLabelProps: lastBeep.importantLabelProps, // labels with high visualization importance
    about: lastBeep.about, // information about the survey
    lineChart: getLineChart(answeredBeeps), // Timeline data
    lineChartWithMissings: getLineChartWithMissings({ allBeeps, table }),
    verticalTable: verticalTable, // Labels are columns and rows are beeps
    questionLists: answeredBeeps.map(({ questionList }) => questionList), // All questionlists (versioning)
    table: table, // all labels
    quanti: quanti, // all quantitative labels
    quali: quali, // all qualitative labels
    openAnswers: openAnswers, // all open labels
    choiceAnswers: choiceAnswers, // all choice labels
    timeAnswers: timeAnswers, // all time labels
    corrMatrix: corrMatrix,
    occurrences: occurrences,
  };
  //console.log("result");
  //console.log(result);
  const t1 = performance.now();
  //console.log(`Time dif ${t1 - t0}`);
  return result;
}
